import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Plus, Edit, Trash2, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase/client';
import { Company } from '@/types/company';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { NewCompanyDialog } from './dialogs/NewCompanyDialog';
import { DeleteCompanyDialog } from './dialogs/DeleteCompanyDialog';

export function CompanyList() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isNewCompanyDialogOpen, setIsNewCompanyDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const fetchCompanies = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('companies')
        .select(`
          *,
          company_themes (*),
          company_domains (*)
        `);
      
      if (error) throw error;
      
      const formattedCompanies: Company[] = data.map(item => ({
        id: item.id,
        name: item.name,
        description: item.description || '',
        status: item.status,
        contactEmail: item.contact_email,
        contactPerson: item.contact_person,
        domains: item.company_domains.map(d => ({
          id: d.id,
          domain: d.domain,
          companyId: d.company_id,
          isPrimary: d.is_primary,
          isActive: d.is_active,
          createdAt: new Date(d.created_at)
        })),
        theme: item.company_themes[0] ? {
          primaryColor: item.company_themes[0].primary_color || '#1E8449',
          secondaryColor: item.company_themes[0].secondary_color || '#F1C40F',
          accentColor: item.company_themes[0].accent_color || '#2C3E50',
          logoUrl: item.company_themes[0].logo_url || null,
          fontPrimary: item.company_themes[0].font_primary || null,
          fontSecondary: item.company_themes[0].font_secondary || null
        } : {
          primaryColor: '#1E8449',
          secondaryColor: '#F1C40F',
          accentColor: '#2C3E50'
        },
        createdAt: new Date(item.created_at),
        updatedAt: new Date(item.updated_at)
      }));
      
      setCompanies(formattedCompanies);
    } catch (error) {
      console.error('Error al cargar empresas:', error);
      toast.error('Error al cargar las empresas');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleAddCompany = async (newCompanyData: Partial<Company>) => {
    try {
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .insert({
          name: newCompanyData.name,
          description: newCompanyData.description,
          status: newCompanyData.status || 'active',
          contact_email: newCompanyData.contactEmail,
          contact_person: newCompanyData.contactPerson
        })
        .select()
        .single();
      
      if (companyError) throw companyError;
      
      const { error: themeError } = await supabase
        .from('company_themes')
        .insert({
          company_id: companyData.id,
          primary_color: newCompanyData.theme?.primaryColor || '#1E8449',
          secondary_color: newCompanyData.theme?.secondaryColor || '#F1C40F',
          accent_color: newCompanyData.theme?.accentColor || '#2C3E50'
        });
      
      if (themeError) throw themeError;
      
      if (newCompanyData.domains && newCompanyData.domains.length > 0) {
        const { error: domainError } = await supabase
          .from('company_domains')
          .insert({
            company_id: companyData.id,
            domain: newCompanyData.domains[0].domain,
            is_active: true,
            is_primary: true
          });
        
        if (domainError) throw domainError;
      }
      
      toast.success('Empresa creada con éxito');
      fetchCompanies();
      setIsNewCompanyDialogOpen(false);
    } catch (error) {
      console.error('Error al crear empresa:', error);
      toast.error('Error al crear la empresa');
    }
  };

  const handleDeleteCompany = async (company: Company) => {
    try {
      const { error } = await supabase
        .from('companies')
        .delete()
        .eq('id', company.id);
      
      if (error) throw error;
      
      toast.success('Empresa eliminada con éxito');
      fetchCompanies();
      setIsDeleteDialogOpen(false);
      setSelectedCompany(null);
    } catch (error) {
      console.error('Error al eliminar empresa:', error);
      toast.error('Error al eliminar la empresa');
    }
  };

  const confirmDeleteCompany = (company: Company) => {
    setSelectedCompany(company);
    setIsDeleteDialogOpen(true);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500 hover:bg-green-600">Activa</Badge>;
      case 'inactive':
        return <Badge variant="secondary">Inactiva</Badge>;
      case 'suspended':
        return <Badge variant="destructive">Suspendida</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Gestión de Empresas</h1>
        <Button onClick={() => setIsNewCompanyDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nueva Empresa
        </Button>
      </div>
      
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map(i => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {companies.map(company => (
            <Card key={company.id}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>{company.name}</CardTitle>
                    <CardDescription>{company.description || 'Sin descripción'}</CardDescription>
                  </div>
                  {getStatusBadge(company.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-sm">
                    <div><span className="font-medium">Contacto:</span> {company.contactEmail || 'No especificado'}</div>
                    <div><span className="font-medium">Dominios:</span> {company.domains?.length || 0}</div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate(`/admin/companies/${company.id}`)}
                    >
                      <Edit className="mr-2 h-4 w-4" />
                      Editar
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate(`/admin/companies/${company.id}/domains`)}
                    >
                      <Globe className="mr-2 h-4 w-4" />
                      Dominios
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => confirmDeleteCompany(company)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Eliminar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {companies.length === 0 && !isLoading && (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-muted-foreground">No hay empresas registradas. Crea una nueva empresa para comenzar.</p>
          </CardContent>
        </Card>
      )}
      
      <NewCompanyDialog 
        open={isNewCompanyDialogOpen}
        onOpenChange={setIsNewCompanyDialogOpen}
        onSubmit={handleAddCompany}
      />
      
      <DeleteCompanyDialog 
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        company={selectedCompany}
        onConfirm={handleDeleteCompany}
      />
    </div>
  );
}
